import { AppDispatch, RootState } from "@/app/stores"
import { IBonusTransactionsAggregates, IGetBonusTransactions } from "@/shared/api/bonuses"
import { getUser, getUserTransactionAggregates, getUserTransactions, IUser } from "@/shared/api/users"
import { userHasCard } from "@/shared/api/users/users"
import { ErrorType } from "@/shared/types"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { AxiosError } from "axios"

export const fetchUserTransactions = createAsyncThunk<
    IGetBonusTransactions,
    void,
    { state: RootState }
>('user/fetchUserTransactions', async (_, thunkAPI) => {
    try {
        const { user, page, limit } = thunkAPI.getState().user
        const data = await getUserTransactions(user.id, {
            startDate: new Date(0),
            endDate: new Date(),
            page,
            limit
        })
        return data.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

export const fetchUserTransactionAggregates = createAsyncThunk<
    IBonusTransactionsAggregates,
    void,
    { state: RootState }
>('user/fetchUserTransactionAggregates', async (_, thunkAPI) => {
    try {
        const { user, startDate, endDate } = thunkAPI.getState().user
        const data = await getUserTransactionAggregates(user.id, startDate, endDate)
        return data.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

export const fetchUser = createAsyncThunk<
    IUser,
    string,
    { state: RootState }
>('user/fetchUser', async (id, thunkAPI) => {
    try {
        const user = await getUser(id)
        return user.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

/**
 * Refresh the profile after a confirmed group change, but only when the page
 * still shows that user's route (the modal is global and its callback can fire
 * after navigation to another profile — pending, fulfilled or failed alike).
 */
export const refreshUserAfterGroupChange = (userId: string) =>
    (dispatch: AppDispatch, getState: () => RootState) => {
        const { user, latestUserRequestId, latestUserRequestArg, routeUserId } = getState().user
        if (user.id !== userId) return
        if (latestUserRequestId && latestUserRequestArg !== userId) return
        if (routeUserId && routeUserId !== userId) return
        void dispatch(fetchUser(userId))
    }

export const fetchUserHasCard = createAsyncThunk<
    { has_card: boolean },
    void,
    { state: RootState }
>('user/fetchUserHasCard', async (_, thunkAPI) => {
    try {
        const { user } = thunkAPI.getState().user
        const data = await userHasCard(user.id)
        return data.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})